import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  IconButton,
  Button,
  Text,
  Icon,
  Tooltip,
	Image,
  Select,
  Spinner,
} from "@chakra-ui/react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaEye,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAgentChat } from "context/AgentChatContext";
import { getScheduledJob } from "services/ScheduleServices";
import { ScheduledJobProto } from "build-ts/schedule";
import { Header } from "build-ts/metadata";
import { AgentInputProto, ChatClientType, ChatRequest } from "build-ts/chat_session";
import { DarwinAgentType } from "build-ts/common";
import { DarwinHeader } from "build-ts/common";
import { getCustomAgent } from "services/CustomAgentServices";
import { CustomAgentProto } from "build-ts/custom_agent";
import { AnalysisTreeProto } from "build-ts/analysis";
import { initializeChatSession } from "services/AgentChatService";
import { ChatMessage } from "./Chat";
import showToast from "hooks/useCustomToast";


interface LeftSideBarChatPageProps {
  isExpanded: boolean;
  onToggle: () => void;
  onViewSystemPrompt?: () => void;
  onSaveChat?: () => void;
  showSystemPromptButton?: boolean;
  isSaving?: boolean;
  canSave?: boolean;
  setAgentId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setRunId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setAgentDetails: React.Dispatch<React.SetStateAction<CustomAgentProto | undefined>>;
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  chatSessionsList?: DarwinHeader[];
  currentChatSessionId?: string;
  onChatSessionClick?: (sessionId: string) => void;
  onChatSessionDelete?: (sessionId: string) => void;
  isFromEvaPlayground?: boolean;
}

const LeftSideBarChatPage: React.FC<LeftSideBarChatPageProps> = ({
  isExpanded,
  onToggle,
  onViewSystemPrompt,
  onSaveChat,
  showSystemPromptButton = false,
  isSaving = false,
  canSave = false,
  setAgentId,
  setRunId,
  setAgentDetails,
  setChatMessages,
  chatSessionsList = [],
  currentChatSessionId,
  onChatSessionClick,
  onChatSessionDelete,
  isFromEvaPlayground = false,
}) => {
  const navigate = useNavigate();
  const sidebarWidth = isExpanded ? "240px" : "48px";
	const {setChatId, agentList, isLoadingAgentList, fetchAgentList} = useAgentChat();

  // State for agent and job run selection
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [selectedJobRun, setSelectedJobRun] = useState<string>("");
  const [jobRuns, setJobRuns] = useState<Header[]>([]);
  const [isLoadingJobRuns, setIsLoadingJobRuns] = useState(false);
  // State for showing/hiding the new chat selection UI
  const [showNewChatSelection, setShowNewChatSelection] = useState(false);

  useEffect(() => {
    fetchAgentList();
  }, []);

  // Handle agent selection
  const handleAgentSelection = async (agentId: string) => {
    setSelectedAgentId(agentId);
    setAgentId(agentId);
    setSelectedJobRun("");
    setJobRuns([]);

    if (agentId) {
      setIsLoadingJobRuns(true);
      try {
        const scheduledJob: ScheduledJobProto | null = await getScheduledJob(agentId, true);
        if (scheduledJob && scheduledJob.job_runs) {
          setJobRuns(scheduledJob.job_runs);
        }
      } catch (error) {
        console.error("Error fetching job runs:", error);
      } finally {
        setIsLoadingJobRuns(false);
      }
    }
  };

  // Handle job run selection
  const handleJobRunSelection = (jobRunId: string) => {
    setSelectedJobRun(jobRunId);
    setRunId(jobRunId);
  };

  const handleInitializeChatSession = async () => {
    const agentDetails = await getCustomAgent(selectedAgentId, [selectedJobRun]);
    if(agentDetails){
      setAgentDetails(agentDetails);
    }
    const chatRequest = ChatRequest.create({
      agent_type: DarwinAgentType.ADD_NUMBERS_TOOL_AGENT,
      client_type: ChatClientType.FRIDA,
      agent_input: AgentInputProto.create({
        trees: agentDetails?.agent_runs[0].agent_run?.tree_headers.map((tree) => AnalysisTreeProto.create({
          header: tree
        })),
        custom_agents: agentDetails ? [agentDetails] : []
      })
    });
    const chatResponse = await initializeChatSession(chatRequest);
    if(chatResponse.chat_id){
      setChatId(chatResponse.chat_id);
      showToast("Chat session initialized successfully", "", "success");
      // Clear messages before navigating - messages will be set by ChatPage if needed
      // or we can set them from chatResponse.messages if available
      setChatMessages([]);
      // Update URL with new chat session ID
      navigate(`/chat/${chatResponse.chat_id}`, { replace: true });
    }
  };

  // Handle navigation to selected job run
  const handleStartChat = async () => {
    if (selectedAgentId && selectedJobRun) {
      // Hide the selection UI after navigation
      await handleInitializeChatSession();
      setShowNewChatSelection(false);
    }
  };

  // Handle starting a new chat
  const handleStartNewChat = () => {
    setShowNewChatSelection(true);
    setChatMessages([]);
    setSelectedAgentId("");
    setSelectedJobRun("");
    setJobRuns([]);
  };

  // Handle canceling new chat selection
  const handleCancelNewChat = () => {
    setShowNewChatSelection(false);
    setSelectedAgentId("");
    setSelectedJobRun("");
    setJobRuns([]);
  };


  const NewChatButton: React.FC = () => {
    if(isFromEvaPlayground) return null;
    if (isExpanded) {
      return (
        <Button
          leftIcon={<Icon as={FaPlus} boxSize={3} />}
          onClick={handleStartNewChat}
          width="100%"
          size="sm"
          bg="white"
          color="gray.700"
          border="1px solid"
          borderColor="gray.400"
          _hover={{ bg: "gray.50", borderColor: "gray.300" }}
          _active={{ bg: "gray.100" }}
          borderRadius="lg"
          fontWeight="medium"
          fontSize="sm"
          py={2}
        >
          Start New Chat
        </Button>
      );
    }

    return (
        <Tooltip label="Start New Chat" placement="right" hasArrow>
          <IconButton
            icon={<Icon as={FaPlus} boxSize={3} />}
            onClick={handleStartNewChat}
            size="sm"
            bg="blue.500"
            color="white"
            _hover={{ bg: "blue.600" }}
            _active={{ bg: "blue.700" }}
            borderRadius="lg"
            aria-label="Start New Chat"
          />
        </Tooltip>
    );
  };

  const ViewSystemPromptButton: React.FC = () => {
    if (!showSystemPromptButton || !onViewSystemPrompt) return null;

    if (isExpanded) {
      return (
        <Button
          leftIcon={<Icon as={FaEye} boxSize={3} />}
          onClick={onViewSystemPrompt}
          width="100%"
          size="sm"
          bg="white"
          color="gray.700"
          border="1px solid"
          borderColor="gray.200"
          _hover={{ bg: "gray.50", borderColor: "gray.300" }}
          _active={{ bg: "gray.100" }}
          borderRadius="lg"
          fontWeight="medium"
          fontSize="sm"
          py={2}
        >
          View System Prompt
        </Button>
      );
    }

    return (
      <Tooltip label="View System Prompt" placement="right" hasArrow>
        <IconButton
          icon={<Icon as={FaEye} boxSize={3} />}
          onClick={onViewSystemPrompt}
          size="sm"
          bg="white"
          color="gray.700"
          border="1px solid"
          borderColor="gray.200"
          _hover={{ bg: "gray.50", borderColor: "gray.300" }}
          _active={{ bg: "gray.100" }}
          borderRadius="lg"
          aria-label="View System Prompt"
        />
      </Tooltip>
    );
  };

  const SaveChatButton: React.FC = () => {
    if (!onSaveChat) return null;

    if (isExpanded) {
      return (
        <Button
          leftIcon={<Icon as={FaSave} boxSize={3} />}
          onClick={onSaveChat}
          width="100%"
          size="sm"
          colorScheme="green"
          isLoading={isSaving}
          loadingText="Saving..."
          isDisabled={!canSave || isSaving}
          borderRadius="lg"
          fontWeight="medium"
          fontSize="sm"
          py={2}
        >
          Save Chat
        </Button>
      );
    }

    return (
      <Tooltip label="Save Chat" placement="right" hasArrow>
        <IconButton
          icon={<Icon as={FaSave} boxSize={3} />}
          onClick={onSaveChat}
          size="sm"
          colorScheme="green"
          isLoading={isSaving}
          isDisabled={!canSave || isSaving}
          borderRadius="lg"
          aria-label="Save Chat"
        />
      </Tooltip>
    );
  };

  return (
    <Box
      position="absolute"
      left="0"
      top="0"
      width={sidebarWidth}
      height="100%"
      borderRight="1px solid"
      borderColor="gray.200"
      transition="width 0.3s ease-in-out"
      zIndex={1000}
      boxShadow="sm"
			bg="bg.surface"
    >
      <VStack spacing={0} align="stretch" height="100%" p={isExpanded ? 3 : 2}>
        {/* Header with toggle */}
        <HStack justify={isExpanded ? "space-between" : "center"} mb={4}>
          {isExpanded && (
            <HStack spacing={2}>
              <Image src="/Anko.png" alt="Anko" boxSize={5} objectFit="contain"/>
							<Text fontSize="sm" fontWeight="semibold" color="gray.800" m={0}>
								Anko
							</Text>
            </HStack>
          )}

          <Tooltip label={isExpanded ? "Close sidebar" : "Open sidebar"} placement="right" hasArrow>
            <IconButton
              icon={<Icon as={isExpanded ? FaChevronLeft : FaChevronRight} boxSize={3} />}
              onClick={onToggle}
              size="sm"
              variant="ghost"
              color="gray.500"
              _hover={{ bg: "gray.100", color: "gray.700" }}
              borderRadius="md"
              aria-label={isExpanded ? "Close sidebar" : "Open sidebar"}
            />
          </Tooltip>
        </HStack>

        {/* New Chat Button */}
        <Box mb={isExpanded ? 4 : 3}>
          <NewChatButton />
        </Box>

        {/* New Chat Selection Section */}
        {isExpanded && showNewChatSelection && (
          <VStack spacing={3} align="stretch">
            {/* Header with Cancel Button */}
            <HStack justify="space-between" align="center">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" m={0}>
                Start New Chat
              </Text>
              <Tooltip label="Cancel" placement="top" hasArrow>
                <IconButton
                  icon={<Icon as={FaTimes} boxSize={3} />}
                  onClick={handleCancelNewChat}
                  size="xs"
                  variant="ghost"
                  color="red.400"
                  _hover={{ bg: "gray.100", color: "gray.700" }}
                  borderRadius="md"
                  aria-label="Cancel new chat"
                />
              </Tooltip>
            </HStack>

            {/* Agent Dropdown */}
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={2} align="left">
                Select Agent
              </Text>
              <Select
                placeholder="Choose an agent..."
                value={selectedAgentId}
                onChange={(e) => handleAgentSelection(e.target.value)}
                size="sm"
                bg="white"
                borderColor="gray.200"
                _hover={{ borderColor: "gray.300" }}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                isDisabled={isLoadingAgentList}
              >
                {agentList.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name || agent.id}
                  </option>
                ))}
              </Select>
              {isLoadingAgentList && (
                <HStack mt={1} spacing={1}>
                  <Spinner size="xs" />
                  <Text fontSize="xs" color="gray.500">Loading agents...</Text>
                </HStack>
              )}
            </Box>

            {/* Job Runs Dropdown */}
            {selectedAgentId && (
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={2} align="left">
                  Select Job Run
                </Text>
                <Select
                  placeholder="Choose a job run..."
                  value={selectedJobRun}
                  onChange={(e) => handleJobRunSelection(e.target.value)}
                  size="sm"
                  bg="white"
                  borderColor="gray.200"
                  _hover={{ borderColor: "gray.300" }}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                  isDisabled={isLoadingJobRuns}
                >
                  {jobRuns.map((jobRun) => (
                    <option key={jobRun.id} value={jobRun.id}>
                      {jobRun.name || jobRun.id}
                    </option>
                  ))}
                </Select>
                {isLoadingJobRuns && (
                  <HStack mt={1} spacing={1}>
                    <Spinner size="xs" />
                    <Text fontSize="xs" color="gray.500">Loading job runs...</Text>
                  </HStack>
                )}
              </Box>
            )}

            {/* Start Chat Button */}
            {selectedJobRun && (
              <VStack spacing={2}>
                <Button
                  onClick={handleStartChat}
                  size="sm"
                  colorScheme="green"
                  width="100%"
                  borderRadius="lg"
                  fontWeight="medium"
                  fontSize="sm"
                >
                  Start Chat
                </Button>
              </VStack>
            )}
          </VStack>
        )}

        {/* View System Prompt Button */}
        {showSystemPromptButton && (
          <Box mb={isExpanded ? 4 : 3}>
            <ViewSystemPromptButton />
          </Box>
        )}

        {/* Save Chat Button */}
        {onSaveChat && (
          <Box mb={isExpanded ? 4 : 3}>
            <SaveChatButton />
          </Box>
        )}

        {/* Chat Sessions List */}
        {isExpanded && chatSessionsList.length > 0 && !showNewChatSelection && !isFromEvaPlayground && (
          <VStack spacing={2} align="stretch" mb={4}>
            <Text fontSize="xs" fontWeight="semibold" color="purple.700" mb={2} align="left" m={0}>
              Recent Chats
            </Text>
            <Box minH="700px" maxH="700px" overflowY="auto" pb={10}>
              {chatSessionsList.map((session) => {
                const isSelected = currentChatSessionId === session.id;
                return (
                <HStack
                  key={session.id}
                  p={2}
                  bg={isSelected ? "purple.100" : "white"}
                  border="1px solid"
                  borderColor={isSelected ? "purple.400" : "purple.200"}
                  borderRadius="md"
                  _hover={{ bg: isSelected ? "purple.100" : "purple.50", borderColor: isSelected ? "purple.400" : "purple.300" }}
                  mb={1}
                  spacing={2}
                >
                  <Box
                    flex="1"
                    cursor="pointer"
                    onClick={() => onChatSessionClick && onChatSessionClick(session.id!)}
                  >
                    <Text fontSize="xs" fontWeight={isSelected ? "semibold" : "medium"} color={isSelected ? "purple.800" : "purple.700"} noOfLines={3} align="left" m={0}>
                      {session.name}
                    </Text>
                  </Box>
                  <Tooltip label="Delete chat" placement="top" hasArrow>
                    <IconButton
                      icon={<Icon as={FaTrash} boxSize={2} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChatSessionDelete && onChatSessionDelete(session.id!);
                      }}
                      size="xs"
                      variant="ghost"
                      color="red.400"
                      _hover={{ bg: "red.50", color: "red.600" }}
                      _active={{ bg: "red.100" }}
                      borderRadius="md"
                      aria-label="Delete chat session"
                    />
                  </Tooltip>
                </HStack>
              );
              })}
            </Box>
          </VStack>
        )}

      </VStack>
    </Box>
  );
};

export default LeftSideBarChatPage;
